import { CSSProperties, useEffect } from 'react';
import { Box, Link, styled, Tooltip } from '@material-ui/core';
import { Customer } from 'Services/MongoRealm/Types';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import { Product, ProductInventory } from '@setvi/shared/interfaces';
import {
  getProductStock,
  getProductStockByCustomer
} from 'pages/products/utils';
import { Variant } from '@material-ui/core/styles/createTypography';
import { Skeleton } from '@material-ui/lab';
import { useRealTimeInventory } from './useRealtimeInventory';

export enum StockColor {
  Green = '#23A566',
  Red = '#FA2020'
}

export const getStock = (
  inventory: Product['inventory'],
  selectedCustomer?: Customer
) => {
  const productStock = getProductStock(inventory);
  const stockForCustomer = getProductStockByCustomer(
    inventory,
    selectedCustomer
  );
  return selectedCustomer ? stockForCustomer : productStock;
};

const StyledLink = styled(Link)({
  wordBreak: 'break-word',
  lineHeight: '24px',
  textDecoration: 'none !important',
  display: 'flex',
  alignItems: 'center',
  gap: 2
});

interface StockProps {
  newDesign?: boolean;
  inventory: Product['inventory'];
  isDropship: boolean;
  variant?: Variant;
  selectedCustomer?: Customer;
  stockInfo?: Product['stockInfo'];
  isStockProduct?: Product['isStockProduct'];
  style?: CSSProperties;
  autoLoad?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /**
   * @param id - The product id to fetch the realtime inventory
   * @param onStockRefresh - The function for update product
   * @description This is used to fetch the realtime inventory for the product
   */
  stockRefresh?: {
    id: string;
    onStockRefresh: (newStockValue: ProductInventory[]) => void;
  };
}

export const Stock = ({
  newDesign,
  inventory,
  isDropship,
  variant = 'body1',
  style = {},
  autoLoad,
  selectedCustomer,
  isStockProduct,
  stockInfo,
  disabled,
  stockRefresh,
  onClick
}: StockProps) => {
  const { fetchRealtimeInventory, loading } = useRealTimeInventory();

  const handleFetchRealtimeInventory = async (id: string) => {
    const res = await fetchRealtimeInventory(id);
    Array.isArray(res) && stockRefresh?.onStockRefresh?.(res);
  };

  const productStock = getProductStock(inventory);

  const stockForCustomer = getProductStockByCustomer(
    inventory,
    selectedCustomer
  );

  const stock = selectedCustomer ? stockForCustomer : productStock;
  const inStock = stock > 0;

  const stockColor = inStock ? StockColor.Green : StockColor.Red;

  const oldStyle = {
    color: disabled ? '#AFBAC6' : stockColor,
    WordBreak: 'break-word',
    cursor: stockRefresh ? 'pointer' : 'default'
  };

  const newStyle = {
    // color: '#333D47',
    fontWeight: 600,
    fontSize: 14
  };

  useEffect(() => {
    if (autoLoad && stockRefresh) {
      handleFetchRealtimeInventory(stockRefresh.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Skeleton width="90px" height="32px" />;

  // This condition returns "-" when there are no products in stock, allWarehouses is false, and inStockProduct is also false.
  // This is because Sysco users sometimes lack information on certain product stock levels, and in such cases, we display "-" instead of "Out of Stock".
  if (
    stock === 0 &&
    stockInfo?.allWarehouses === false &&
    isStockProduct === false
  )
    return '-';

  if (isDropship)
    return (
      <Tooltip
        arrow
        title="It is not stocked in the distributor's warehouse; instead, it is shipped directly from the vendor.">
        <Link
          variant={variant}
          underline="always"
          color="primary"
          style={{ cursor: onClick ? 'pointer' : 'default', ...style }}
          onClick={e => {
            e.stopPropagation();
            onClick?.();
          }}>
          Drop ship
        </Link>
      </Tooltip>
    );

  return (
    <Tooltip
      arrow
      placement="top"
      style={{ width: 'fit-content' }}
      title={
        <Box p={1}>
          <Box pb={1}>
            <b>Available in Warehouse</b>
          </Box>
          <ul
            style={{
              padding: 0,
              margin: 0,
              listStyle: 'none'
            }}>
            {inventory?.length === 0 && (
              <li>No stock information available. </li>
            )}
            {inventory?.map(({ warehouse, availableOnHand }) => (
              <li key={warehouse?._id}>
                {warehouse?.name} - In stock: {availableOnHand}
              </li>
            ))}
          </ul>
        </Box>
      }>
      <StyledLink
        variant={variant}
        style={{
          ...(newDesign && newStyle),
          ...oldStyle,
          ...style
        }}
        onClick={e => {
          e.stopPropagation();
          stockRefresh
            ? handleFetchRealtimeInventory(stockRefresh.id)
            : onClick?.();
        }}>
        {inStock ? (
          <>
            In stock: {stock}
            <InfoOutlinedIcon style={{ fontSize: 14 }} />
          </>
        ) : (
          'Out of stock'
        )}
      </StyledLink>
    </Tooltip>
  );
};
